import images from '@assets/images'
import SkillSetCharts from '@components/Charts/GoalIndex/SkillSetCharts'
import { useQuery } from '@tanstack/react-query'
import { Flex, Spin } from 'antd'
import { useEffect, useState } from 'react'

import { Link } from '@suntex.core/nav'
import { gradeToPhase } from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $EntityLevel,
  $GoalsIndexPhase,
  $SpecialGradeLevel,
  EntityLevel,
  GoalsIndexPhase,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { SkillSetInfoModalContent } from '@suntex.fim/instructions'
import { useEntityContext } from '@suntex.fim/nav'
import { Button, LevelSelect, Modal, Tooltip } from '@suntex.fim/ui'

const levelOptions = [
  {
    key: GoalsIndexPhase.Early,
    label: 'Early (SS 1-2)'
  },
  {
    key: GoalsIndexPhase.Elementary,
    label: 'Elementary (SS 3-4)'
  },
  {
    key: GoalsIndexPhase.Intermediate,
    label: 'Intermediate (SS 5-6)'
  },
  {
    key: GoalsIndexPhase.Advanced,
    label: 'Advanced (SS 7-8)'
  }
]
function SkillSet({
  id,
  year,
  grade,
  defaultGrade,
  entityLevel,
  skillSetLevel,
  onLevelChange
}: {
  id?: number
  year?: string
  grade?: SpecialGradeLevel
  defaultGrade?: SpecialGradeLevel
  entityLevel?: EntityLevel
  skillSetLevel?: GoalsIndexPhase | null
  onLevelChange?: (level: GoalsIndexPhase) => void
}) {
  const { login } = useAuth(isAuthenticated)
  const { api } = useFramework()
  const [skillSetModal, setSkillSetModal] = useState(false)
  const [level, setLevel] = useState<GoalsIndexPhase>(
    skillSetLevel ?? GoalsIndexPhase.Early
  )
  const { entityLevel: apiLevel, entityId: apiId } = useEntityContext()

  const role = $EntityLevel.describe(apiLevel).slug

  const { data, isLoading } = useQuery({
    queryKey: ['assessment', 'goalsInfoSkillset', apiId, apiLevel, year, grade],
    queryFn: async () =>
      (
        await api.get_Assessment_GoalsInfoSkillset({
          id: apiId!,
          level: apiLevel,
          year: year!,
          grade: grade!
        })
      ).ss,
    enabled: apiId != null && year != null && grade != null
  })

  useEffect(() => {
    if (onLevelChange) {
      onLevelChange(level) // Notify parent of level changes
    }
  }, [level, onLevelChange])

  useEffect(() => {
    const g = defaultGrade ?? grade
    if (g != null && skillSetLevel === null) {
      setLevel(gradeToPhase(g))
    }
    if (g == null && skillSetLevel === null)
      setLevel(
        gradeToPhase(
          $SpecialGradeLevel.fromOptional(login.gradeId) ??
            SpecialGradeLevel.AllGrades
        )
      )
  }, [defaultGrade, grade, skillSetLevel, login.gradeId])

  return (
    <Flex vertical gap="20px" className="graph-tiles team-activity">
      <Flex
        align="center"
        className="graph-tiles__title pdf-title"
        justify="space-between"
      >
        <Flex gap="10px">
          {entityLevel !== EntityLevel.Player ? (
            <Link
              className="report-link"
              to={`/${role}/${id}/assessment-report/skill-set-games`}
            >
              <h4 className="title">Skill Sets - Procedural Fluency</h4>
            </Link>
          ) : (
            <h4 className="title">Skill Sets - Procedural Fluency</h4>
          )}
          <Button
            aria-label="Skill Sets information"
            intent="icon"
            onClick={() => setSkillSetModal(true)}
          >
            <images.InfoCircle />
          </Button>
        </Flex>
        <div className="right">
          {entityLevel !== EntityLevel.Player && (
            <Link
              className="print-hidden"
              to={`/${role}/${id}/assessment-report/skill-set-games`}
            >
              <Tooltip
                classNames={{ root: 'progress-tooltip' }}
                title={
                  <div>
                    Click here to drill down into the Skill Sets report for this{' '}
                    {role}
                  </div>
                }
                arrow={false}
                placement="bottom"
              >
                <img
                  src={images.Ss}
                  alt="Skill-set"
                  className="goal-index-report-icon"
                />
              </Tooltip>
            </Link>
          )}
          <LevelSelect
            value={level}
            onChange={(level) => setLevel(level)}
            options={levelOptions}
          />
        </div>
      </Flex>
      <Flex vertical align="left">
        <div className="graph-container">
          {isLoading ? (
            <Spin />
          ) : (
            data && (
              <SkillSetCharts
                backgroundColor="#E0646B"
                level={level}
                chartData={data}
              />
            )
          )}
        </div>
      </Flex>
      {skillSetModal && (
        <Modal
          closable={false}
          centered
          open
          onOk={() => setSkillSetModal(false)}
          onCancel={() => setSkillSetModal(false)}
          width={900}
          className="skillsets-modal"
          footer={[
            <Button
              shape="round"
              intent="tertiary"
              key="submit"
              onClick={() => setSkillSetModal(false)}
            >
              Close
            </Button>
          ]}
        >
          <SkillSetInfoModalContent />
        </Modal>
      )}
    </Flex>
  )
}

export default SkillSet
